(ns machtfit.dashboard.stepchall.form.route-editor.point-weight
  (:require [machtfit.stepchall.catmullrom :as catmullrom]))

(defn- path-length [leg-lengths index]
  (->> leg-lengths
       (take index)
       (reduce +)))

(defn effective-stage-points
  "Given a path, goal, and stages with num-steps this finds the relevant
   stage points in the path, which then can be used to calculate the leg
   weights between those stage points.

   It returns a sequence of [path-point-index geometrical-path-length num-steps].

   The first and last path points are special and whether they are stage points
   or not should be ignored.

   The reason is this: If we find ourselves in a situation where the number of
   marked stage points is lower than the number of stages, but the final point is
   marked as a stage point, then some of the assumptions of the algorithm below
   and the algorithm to calculate progress along the route of the weighed path
   don't hold anymore, as there'd be a 0 length 'rest' path after the stage that
   would end up on the last point, which would have to account for all points
   between that stage and the final stage.

   Similarly the first stage being on a point other than the first in the path
   will break some assumptions and result in meaningless results, as it isn't
   clear what the weight of the path leading up to the first stage (with num
   steps 0) should be.

   To mitigate this, we can simply ignore the first and last path points,
   whether they have a stage marker or not, and always use fixed stage points
   for start and finish, which correspond to the dropped points."
  [{:keys [path goal stages]}]
  (let [;; we need to reset the weights of all path points before calculating
        ;; the leg lengths
        path (map #(assoc % :d 1) path)
        leg-lengths (catmullrom/path->leg-lengths path)
        last-point-index (dec (count path))
        ;; only consider stages with num-steps >= 0, which ignores those
        ;; with a negative number of steps or no steps at all;
        ;; also remove duplicates
        relevant-stage-steps (->> stages
                                  (map :num-steps)
                                  (cons 0)
                                  (cons goal)
                                  (filter #(some-> % (>= 0)))
                                  sort
                                  distinct
                                  vec)]
    (-> (->> path
             (keep-indexed (fn [index point]
                             ;; consider a path point a stage point
                             ;; if it is the first one or if it is
                             ;; marked as a stage point, but isn't the
                             ;; last path point, because that one will
                             ;; be appended separately
                             (when (or (zero? index)
                                       (and (not= index last-point-index)
                                            (:stage? point)))
                               index)))
             (map-indexed (fn [stage-index path-index]
                            [path-index
                             (path-length leg-lengths path-index)
                             (get relevant-stage-steps stage-index)]))
             ;; Here we only take one less stage point than we need, because
             ;; the stage point for the final stage will be the last path point.
             ;; This is important if the user marks more stage points than necessary,
             ;; even if only temporarily, which would result in the goal ending
             ;; up somewhere along the path, rather than the last path point.
             (take (dec (count relevant-stage-steps))))
        vec
        ;; add the last stage point as a fixed point
        (conj [last-point-index (path-length leg-lengths last-point-index) goal]))))

(defn- calculate-weight-between-stage-points
  "Calculate the weight of points between two stage points.

   The result gives the weight and a start-index from which
   on the weight applies."
  [[[previous-path-index
     previous-path-length
     previous-num-steps]
    [_current-path-index
     current-path-length
     current-num-steps]]]
  (let [path-length-diff (- current-path-length previous-path-length)
        num-steps-diff (- current-num-steps previous-num-steps)
        ;; Here path-length-diff should be greater than zero due to
        ;; previous-path-index < path-index, but it's still possible
        ;; that the path contains two consecutive points that
        ;; are equal, if they both are also stage points, then
        ;; just fall back to a weight of 1.
        ;; However, this is a trivial edge case, we only need to
        ;; handle it to avoid crashes if it comes up while editing
        ;; by accident.
        weight (if (zero? path-length-diff)
                 1
                 (/ num-steps-diff
                    path-length-diff))]
    ;; increment previous-path-index, because the weight only applies
    ;; after it
    [(inc previous-path-index) weight]))

(defn- apply-new-weights
  "Given a path and a sequence of [start-index weight], set the
   weight on each point in the path, based on which weight range its
   index falls into.

   The weight of a point is associated with its key :d."
  [path point-weight-start-indices]
  (let [first-leg-weight (-> point-weight-start-indices first second)]
    (->> path
         (map-indexed
          (fn [index point]
            (let [weight (or (->> point-weight-start-indices
                                  (take-while #(-> % first (<= index)))
                                  last
                                  second)
                             1)]
              (assoc point :d (/ weight first-leg-weight)))))
         vec)))

(defn calculate
  "Calculate the weights for each point in such a way that any N-th stage will end up on
   the N-th path point marked as `stage?`, based on its num-steps.
   The algorithm first determines each path index and stage index for all those special
   points, as well as the target number of steps, then calculates the difference in
   path length (based on Euclid distance of the sequence of points) and step distance
   between consecutive stage points, then num-steps-diff / path-length-diff is the weight
   for all the points between those stage points.
   Finally, the weight for each point :d is set to the calculated point, divided by the
   weight of the first point to normalize the values.

   The weights of points between stage points therefore is constant, resulting in linear
   progress, no matter what the path looks like.

   We don't need to calculate any weights if there are only two effective stage points:
   the first and last point of the path, in that case we leave the weights as they are,
   which preserves the preset weights of theme paths and still allows path manipulation.
   However, whenever stage points are manipulated (added or removed), we need to calculate
   the weights no matter how many stage points remain, for this the `force?` argument can
   be passed to force calculation."
  [db & {:keys [force?]}]
  (let [path (-> db :challenge :theme :path)
        stage-points (effective-stage-points {:path path
                                              :stages (-> db :challenge :config :stages)
                                              :goal (-> db :challenge :config :goal)})
        calculate-weights? (or force?
                               (> (count stage-points)
                                  2))]
    (if calculate-weights?
      (assoc-in
       db
       [:challenge :theme :path]
       (let [point-weight-start-indices
             (->> stage-points
                  (partition 2 1)
                  (mapv calculate-weight-between-stage-points))]
         (apply-new-weights path point-weight-start-indices)))
      db)))
